import { API_CONFIG } from "@/infrastructure/config/api";
import {
  createAuthenticatedSSE,
  type SSESubscription,
} from "@/shared/utils/fetchSSE";
import { get } from "@/shared/utils/fetchApi";


export interface AssistantLogLine {
  id: string;
  timestamp: string;
  level: string;
  message: string;
}

export interface AssistantLogsResponse {
  lines?: AssistantLogLine[];
  raw?: string;
  logFiles?: string[];
  message?: string;
}

export class AssistantLogsService {
  /**
   * Busca lista de arquivos de log disponíveis ou conteúdo de um arquivo específico.
   *
   * Sem `file`: aciona o caminho de listagem no Assistant (`GET /api/logs`) que
   * devolve `{ logFiles: [...] }`. **Importante**: nesse caso `tail` NÃO é
   * enviado — o Assistant interpreta a presença de `tail` (mesmo sem `file`)
   * como "leia o arquivo de hoje", retornando texto e nunca a lista.
   *
   * Com `file`: pede o conteúdo daquele arquivo, com `tail` linhas no final.
   */
  static async getLogs(
    file?: string,
    tail: number = 200,
  ): Promise<AssistantLogsResponse> {
    const params = new URLSearchParams();
    if (file) {
      params.append("file", file);
      params.append("tail", tail.toString());
    }
    const qs = params.toString();
    return get<AssistantLogsResponse>(
      qs ? `/assistant/logs?${qs}` : "/assistant/logs",
    );
  }

  /**
   * Cria uma conexão SSE para receber logs em tempo real
   * @param onLog Callback para cada nova linha de log
   * @param onConnect Callback executado quando a conexão é estabelecida
   * @param onError Callback para erros na conexão
   * @param file Nome do arquivo de log (opcional, usa o atual por padrão)
   * @param tail Número de linhas iniciais a carregar
   * @returns EventSource para controle da conexão
   */
  static createLogStream(
    onLog: (log: AssistantLogLine) => void,
    onConnect?: (data: unknown) => void,
    onError?: (event: Event) => void,
    file?: string,
    tail: number = 200,
  ): SSESubscription {
    const params = new URLSearchParams();
    params.append("follow", "true");
    params.append("tail", tail.toString());
    if (file) params.append("file", file);

    const url = `${API_CONFIG.API_ROOT_URL}/assistant/logs?${params.toString()}`;

    return createAuthenticatedSSE(url, {
      onOpen: () => {
        if (onConnect) {
          onConnect({ status: "connected" });
        }
      },
      onMessage: (data, eventType) => {
        if (eventType === "connected") {
          try {
            const parsed = JSON.parse(data);
            if (onConnect) onConnect(parsed);
          } catch {
            // Non-JSON connected event — safe to ignore
          }
          return;
        }

        if (eventType === "heartbeat") {
          return;
        }

        if (eventType === "log") {
          try {
            const parsed = JSON.parse(data);

            // If the backend sends structured fields, use them directly
            if (parsed.timestamp && parsed.level) {
              onLog({
                id: crypto.randomUUID(),
                timestamp: parsed.timestamp,
                level: this.normalizeLogLevel(parsed.level),
                message: parsed.message || String(parsed),
              });
              return;
            }

            // Backend sends {"line":"[2026-04-28 11:15:48 ERR] message..."} — parse the line
            const rawLine = parsed.line || String(parsed);
            const logLine = this.parseLogLine(rawLine.replace(/\r+$/, ""));
            if (logLine) onLog(logLine);
          } catch {
            // Non-JSON log event — try parsing as plain text
            const logLine = this.parseLogLine(data);
            if (logLine) onLog(logLine);
          }
          return;
        }

        // Generic message event
        try {
          const parsed = JSON.parse(data);
          if (typeof parsed === "string") {
            const logLine = this.parseLogLine(parsed);
            if (logLine) onLog(logLine);
          }
        } catch {
          const logLine = this.parseLogLine(data);
          if (logLine) onLog(logLine);
        }
      },
      onError: (error) => {
        if (onError) {
          onError(new ErrorEvent("error", { error }));
        }
      },
    });
  }

  /**
   * Busca logs de uma reunião específica
   * @param url URL da reunião
   * @param tail Número máximo de linhas (padrão: 500)
   * @returns Promise com logs da reunião
   */
  static async getMeetingLogs(
    url: string,
    tail: number = 500,
  ): Promise<AssistantLogsResponse> {
    const params = new URLSearchParams();
    params.append("url", url);
    params.append("tail", tail.toString());

    const qs = params.toString();
    return get<AssistantLogsResponse>(`/assistant/logs/meeting?${qs}`);
  }

  /**
   * Busca timeline estruturada de eventos de uma reunião
   * @param url URL da reunião (opcional)
   * @param meetingId ID da reunião (opcional)
   * @param tail Número máximo de eventos (padrão: 1000)
   * @returns Promise com timeline de eventos
   */
  static async getMeetingTimeline(
    url?: string,
    meetingId?: string,
    tail: number = 1000,
  ): Promise<unknown> {
    const params = new URLSearchParams();
    if (url) params.append("url", url);
    if (meetingId) params.append("meetingId", meetingId);
    params.append("tail", tail.toString());

    const qs = params.toString();
    return get<unknown>(`/assistant/logs/meeting/timeline?${qs}`);
  }

  /**
   * Normaliza níveis de log para formato padrão
   */
  private static normalizeLogLevel(level: string): string {
    const upper = level.toUpperCase();
    switch (upper) {
      case "INF":
        return "INFO";
      case "WRN":
      case "WARN":
        return "WARN";
      case "ERR":
      case "ERROR":
        return "ERROR";
      case "DBG":
      case "DEBUG":
        return "DEBUG";
      default:
        return upper;
    }
  }

  /**
   * Parse uma linha de log em formato de texto
   * Formato esperado: [2025-09-07 17:07:18 INF] [CATEGORY] Mensagem
   */
  private static parseLogLine(line: string): AssistantLogLine | null {
    const logRegex =
      /^\[(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})\s*(INF|WRN|ERR|DBG|INFO|WARN|ERROR|DEBUG)?\]\s*(.*)$/i;
    const match = line.match(logRegex);

    if (match) {
      const [, timestamp, lvl, message] = match;
      return {
        id: crypto.randomUUID(),
        timestamp: timestamp.trim(),
        level: this.normalizeLogLevel(lvl || "INFO"),
        message: message.trim(),
      };
    }

    // Se não corresponde ao formato, retornar como mensagem genérica
    if (line.trim()) {
      return {
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString().replace("T", " ").slice(0, 19),
        level: "INFO",
        message: line.trim(),
      };
    }

    return null;
  }
}
